import { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import API, { authFetch } from './api';

function BuyerHome({ user }) {
  const [topMatches, setTopMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadBuyerData(); }, []);

  const loadBuyerData = async () => {
    try {
      if (user?.buyer_id) {
        const boardRes = await authFetch(`${API}/api/board/buyer/${user.buyer_id}`);
        if (boardRes.ok) {
          const data = await boardRes.json();
          setTopMatches(Array.isArray(data) ? data.slice(0, 5) : []);
        }
      }
    } catch (err) {
      console.error('Buyer dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (p) => p ? `$${Number(p).toLocaleString()}` : 'N/A';

  const getTierColor = (tier) => {
    const colors = {
      'ELITE FIT': 'text-emerald-300',
      'STRONG FIT': 'text-green-300',
      'GOOD FIT': 'text-yellow-300',
      'MARGINAL FIT': 'text-orange-300',
      'POOR FIT': 'text-red-300',
    };
    return colors[tier] || 'text-gray-400';
  };

  const firstName = user?.full_name?.split(' ')[0] || 'there';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center">
        <div className="text-[#00D4FF]">Loading your dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0E27] pt-8 pb-12">
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">
          Welcome back, <span className="text-[#00E676]">{firstName}</span>
        </h1>
        <p className="text-[#B0BEC5] mb-8">Your personalized property matches</p>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <Link to="/match-board" className="bg-[#1A2744] rounded-xl border border-[#00D4FF]/30 p-4 md:p-6 hover:border-[#00D4FF] transition-colors">
            <h3 className="text-white font-semibold text-lg mb-2">Your Match Board</h3>
            <p className="text-gray-400 text-sm mb-3">See all properties ranked by how well they match your priorities.</p>
            <span className="text-[#00D4FF] text-sm font-medium">View Match Board →</span>
          </Link>
          <Link to={`/buyers/${user?.buyer_id}/criteria`} className="bg-[#1A2744] rounded-xl border border-[#00E676]/30 p-4 md:p-6 hover:border-[#00E676] transition-colors">
            <h3 className="text-white font-semibold text-lg mb-2">My Criteria & Weights</h3>
            <p className="text-gray-400 text-sm mb-3">Adjust what matters most to you — schools, price, commute, and more.</p>
            <span className="text-[#00E676] text-sm font-medium">Edit My Criteria →</span>
          </Link>
        </div>

        {/* Top Matches */}
        <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-white font-semibold text-lg">Your Top Matches</h2>
            <Link to="/match-board" className="text-[#00D4FF] text-sm hover:text-[#00E676]">View All →</Link>
          </div>

          {topMatches.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-400 mb-2">No matches yet.</p>
              <p className="text-gray-500 text-sm">Set up your criteria and properties will be scored automatically.</p>
              <Link to={`/buyers/${user?.buyer_id}/criteria`} className="inline-block mt-4 px-4 py-2 bg-[#00D4FF] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00B8D4]">
                Set Up My Criteria
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {topMatches.map((match, i) => (
                <Link key={match.property_id} to={`/properties/${match.property_id}/buyer/${user.buyer_id}`}
                  className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-0 p-4 bg-[#0A0E27]/50 rounded-lg hover:bg-[#243B55]/30 transition-colors">
                  <div className="flex items-center gap-3 md:gap-4 min-w-0">
                    <span className="text-[#00D4FF] font-bold text-lg w-8 shrink-0">#{i + 1}</span>
                    <div className="min-w-0">
                      <div className="text-white font-medium truncate">{match.address}</div>
                      <div className="text-gray-500 text-xs">
                        {match.city}, {match.state} · {match.beds}/{match.baths} · {match.sqft?.toLocaleString()} sqft
                      </div>
                    </div>
                  </div>
                  <div className="text-right pl-11 md:pl-0">
                    <div className="text-[#00E676] font-bold">{formatPrice(match.price)}</div>
                    <div className={`text-sm font-medium ${getTierColor(match.tier)}`}>
                      {match.fit_score ? Math.round((match.fit_score / 9) * 100) : 0}% — {match.tier}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Tip */}
        <div className="mt-6 bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4">
          <p className="text-gray-400 text-sm">
            <span className="text-[#00D4FF] font-medium">Tip:</span> Adjust your criteria weights to see how different priorities change your property rankings. Properties are rescored automatically when you make changes.
          </p>
        </div>
      </div>
    </div>
  );
}

const ACTIVITY_STYLES = {
  buyer_signup:      { dot: 'bg-[#00E676]',           text: 'text-[#00E676]' },
  note_added:        { dot: 'bg-[#00D4FF]',           text: 'text-[#00D4FF]' },
  grade_override:    { dot: 'bg-green-400',           text: 'text-green-400' },
  report_generated:  { dot: 'bg-purple-400',          text: 'text-purple-400' },
  property_imported: { dot: 'bg-white',               text: 'text-white' },
  invite_created:    { dot: 'bg-blue-400',            text: 'text-blue-400' },
  invite_redeemed:   { dot: 'bg-[#00E676]',           text: 'text-[#00E676]' },
  criteria_updated:  { dot: 'bg-yellow-400',          text: 'text-yellow-400' },
};

function relativeTime(isoString) {
  if (!isoString) return '';
  const then = new Date(isoString.replace(' ', 'T'));
  if (isNaN(then.getTime())) return '';
  const diffSec = Math.max(0, Math.floor((Date.now() - then.getTime()) / 1000));
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr !== 1 ? 's' : ''} ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return 'Yesterday';
  if (diffDay < 7) return `${diffDay} days ago`;
  return then.toLocaleDateString();
}

function ActivityItem({ item }) {
  const style = ACTIVITY_STYLES[item.activity_type] || { dot: 'bg-gray-400', text: 'text-gray-300' };
  const summary = item.summary || '';

  // Build the rendered summary by splicing in links for buyer_name and property_address when those substrings appear.
  const parts = [];
  let remaining = summary;
  const tokens = [];
  if (item.buyer_name && item.buyer_id) {
    tokens.push({
      text: item.buyer_name,
      el: <Link key="b" to={`/match-board?buyer=${item.buyer_id}`} className={`${style.text} hover:underline`}>{item.buyer_name}</Link>,
    });
  }
  if (item.property_address && item.property_id) {
    tokens.push({
      text: item.property_address,
      el: <Link key="p" to={`/properties/${item.property_id}`} className="text-white hover:underline">{item.property_address}</Link>,
    });
  }
  // Greedy left-to-right match: find earliest occurrence of any token, splice it.
  let safety = 0;
  while (remaining && safety < 10) {
    safety++;
    let bestIdx = -1, bestTok = null;
    for (const t of tokens) {
      const i = remaining.indexOf(t.text);
      if (i !== -1 && (bestIdx === -1 || i < bestIdx)) { bestIdx = i; bestTok = t; }
    }
    if (bestIdx === -1) {
      parts.push(remaining);
      remaining = '';
    } else {
      if (bestIdx > 0) parts.push(remaining.slice(0, bestIdx));
      parts.push(bestTok.el);
      remaining = remaining.slice(bestIdx + bestTok.text.length);
    }
  }
  if (remaining) parts.push(remaining);

  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-[#546E7A]/10 last:border-0">
      <span className={`shrink-0 w-2 h-2 rounded-full mt-2 ${style.dot}`}></span>
      <div className="flex-1 min-w-0">
        <p className="text-gray-200 text-sm leading-snug">{parts.map((p, i) => typeof p === 'string' ? <span key={i}>{p}</span> : p)}</p>
        <p className="text-gray-500 text-xs mt-0.5">{relativeTime(item.created_at)}</p>
      </div>
    </div>
  );
}

function AgentHome({ user }) {
  const [buyers, setBuyers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [recentGrades, setRecentGrades] = useState([]);
  const [activity, setActivity] = useState([]);
  const [activityHasMore, setActivityHasMore] = useState(false);
  const [loading, setLoading] = useState(true);

  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState('');
  const [insightsExpanded, setInsightsExpanded] = useState(true);

  const loadInsights = async () => {
    setInsightsLoading(true);
    setInsightsError('');
    try {
      const res = await authFetch(`${API}/api/insights/daily`);
      const data = await res.json();
      if (!res.ok) {
        setInsightsError(data.detail || 'Could not generate insights');
      } else {
        setInsights(data);
        setInsightsExpanded(true);
      }
    } catch (err) {
      setInsightsError('Network error');
    } finally {
      setInsightsLoading(false);
    }
  };

  const handlePrintInsights = () => {
    if (!insights || !insights.actions || insights.actions.length === 0) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const escapeHtml = (s) => String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

    const today = new Date().toLocaleDateString(undefined, {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });
    const agentName = escapeHtml(user?.full_name || 'Agent');

    const cards = insights.actions.map(a => {
      const p = Math.max(1, Math.min(5, parseInt(a.priority, 10) || 5));
      const buyer = a.buyer_name
        ? `<span class="buyer-name">${escapeHtml(a.buyer_name)}</span> `
        : '';
      const reasoning = a.reasoning
        ? `<p class="reasoning">${escapeHtml(a.reasoning)}</p>`
        : '';
      return `
        <div class="action-card priority-${p}">
          <div class="priority-num">${p}</div>
          <div class="action-content">
            <p class="action-text">${buyer}${escapeHtml(a.action)}</p>
            ${reasoning}
          </div>
        </div>`;
    }).join('');

    printWindow.document.write(`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Daily Action Plan — ${agentName}</title>
  <style>
    * { box-sizing: border-box; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif;
      max-width: 800px;
      margin: 0 auto;
      padding: 40px;
      color: #1a1a1a;
      background: #ffffff;
      line-height: 1.5;
    }
    .header {
      border-bottom: 2px solid #00BCD4;
      padding-bottom: 16px;
      margin-bottom: 24px;
    }
    .header .label {
      font-size: 11px;
      color: #888;
      letter-spacing: 2px;
      text-transform: uppercase;
    }
    .header h1 {
      font-size: 24px;
      margin: 6px 0 4px 0;
      color: #1a1a1a;
    }
    .header .date {
      color: #555;
      font-size: 14px;
    }
    .action-card {
      display: flex;
      align-items: flex-start;
      gap: 14px;
      border-left: 4px solid #9CA3AF;
      background: #fafafa;
      padding: 14px 18px;
      margin-bottom: 12px;
      border-radius: 0 6px 6px 0;
      page-break-inside: avoid;
    }
    .action-card.priority-1 { border-left-color: #00C853; background: #f0fbf3; }
    .action-card.priority-2,
    .action-card.priority-3 { border-left-color: #0097A7; background: #f0fafc; }
    .action-card.priority-4,
    .action-card.priority-5 { border-left-color: #9CA3AF; background: #fafafa; }
    .priority-num {
      flex-shrink: 0;
      width: 30px;
      height: 30px;
      border-radius: 50%;
      font-weight: 700;
      font-size: 13px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid #ccc;
      background: #ffffff;
    }
    .priority-1 .priority-num { color: #00833D; border-color: #00C853; }
    .priority-2 .priority-num,
    .priority-3 .priority-num { color: #006978; border-color: #0097A7; }
    .priority-4 .priority-num,
    .priority-5 .priority-num { color: #555; border-color: #9CA3AF; }
    .action-content { flex: 1; min-width: 0; }
    .action-text {
      margin: 0 0 6px 0;
      font-size: 14px;
      color: #1a1a1a;
    }
    .buyer-name {
      font-weight: 600;
      color: #0097A7;
    }
    .reasoning {
      margin: 0;
      font-size: 12px;
      color: #555;
    }
    .footer {
      margin-top: 40px;
      padding-top: 16px;
      border-top: 1px solid #ddd;
      font-size: 11px;
      color: #999;
      text-align: center;
    }
    @media print {
      body { padding: 24px; }
      .action-card,
      .priority-num {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
    }
  </style>
</head>
<body>
  <div class="header">
    <div class="label">SchemeSync RE — Daily Action Plan</div>
    <h1>Daily Action Plan for ${agentName}</h1>
    <div class="date">${escapeHtml(today)}</div>
  </div>
  ${cards}
  <div class="footer">Generated by SchemeSync RE — re.aida-advisory.com</div>
</body>
</html>`);
    printWindow.document.close();
    printWindow.print();
  };

  useEffect(() => { loadDashboard(); }, []);

  const loadDashboard = async () => {
    try {
      const [buyersRes, propsRes, recentRes, activityRes] = await Promise.all([
        authFetch(`${API}/api/buyers`),
        authFetch(`${API}/api/properties?limit=10`),
        authFetch(`${API}/api/grades/recent?limit=5`),
        authFetch(`${API}/api/activity?limit=16`),
      ]);
      if (buyersRes.ok) setBuyers(await buyersRes.json());
      if (propsRes.ok) setProperties(await propsRes.json());
      if (recentRes.ok) setRecentGrades(await recentRes.json());
      if (activityRes.ok) {
        const data = await activityRes.json();
        setActivity(data.slice(0, 15));
        setActivityHasMore(data.length > 15);
      }
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const firstName = user?.full_name?.split(' ')[0] || 'there';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center">
        <div className="text-[#00D4FF]">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0E27] pt-8 pb-12">
      <div className="max-w-5xl mx-auto px-4 md:px-6">
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">
          Welcome back, <span className="text-[#00E676]">{firstName}</span>
        </h1>
        <p className="text-[#B0BEC5] mb-6">Your Property Evaluation Dashboard</p>

        {/* Broker Dashboard CTA — broker and super_admin only */}
        {(user?.role === 'broker' || user?.role === 'super_admin') && (
          <Link
            to="/broker-dashboard"
            className="block bg-[#1A2744] rounded-xl border border-[#00D4FF]/30 p-4 md:p-6 mb-6 hover:border-[#00D4FF] transition-colors group"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-white font-semibold text-lg">Broker Dashboard</h3>
                <p className="text-gray-400 text-sm mt-1">Aggregate stats across all your agents — readiness pipeline, performance ranking, and team-wide activity.</p>
              </div>
              <span className="text-[#00D4FF] text-sm font-medium whitespace-nowrap group-hover:translate-x-0.5 transition-transform">View Dashboard →</span>
            </div>
          </Link>
        )}

        {/* AI Insights — What Should I Do Today? */}
        <div className="mb-8">
          {!insights && !insightsLoading && !insightsError && (
            <button
              onClick={loadInsights}
              className="w-full md:w-auto px-5 py-3 bg-gradient-to-r from-[#00D4FF] to-[#00E676] text-[#0A0E27] font-semibold rounded-lg hover:opacity-90 transition-opacity shadow-lg shadow-[#00D4FF]/20"
            >
              ✨ What Should I Do Today?
            </button>
          )}

          {insightsLoading && (
            <div className="bg-[#1A2744] rounded-xl border border-[#00D4FF]/30 p-4 md:p-5">
              <div className="flex items-center gap-3">
                <span className="text-xl">✨</span>
                <div>
                  <div className="text-[#00D4FF] text-sm font-medium">Analyzing your portfolio...</div>
                  <div className="text-gray-500 text-xs mt-0.5">Looking at your buyers, recent activity, and inventory.</div>
                </div>
              </div>
            </div>
          )}

          {insightsError && !insightsLoading && (
            <div className="bg-red-900/30 border border-red-700/50 text-red-300 px-4 py-3 rounded-lg text-sm flex justify-between items-center gap-3 flex-wrap">
              <span>{insightsError}</span>
              <button onClick={loadInsights} className="text-red-200 underline hover:text-white text-xs">Retry</button>
            </div>
          )}

          {insights && !insightsLoading && (
            <div>
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                {insights.actions.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => setInsightsExpanded(v => !v)}
                    className="flex items-center gap-2 text-white font-semibold hover:text-[#00D4FF] transition-colors"
                    aria-expanded={insightsExpanded}
                  >
                    <span>✨</span>
                    <span>
                      Daily Action Plan
                      {!insightsExpanded && ` (${insights.actions.length} action${insights.actions.length !== 1 ? 's' : ''})`}
                    </span>
                    <span className={`inline-block text-xs transition-transform duration-200 ${insightsExpanded ? 'rotate-90' : ''}`}>▶</span>
                  </button>
                ) : (
                  <h2 className="text-white font-semibold flex items-center gap-2">
                    <span>✨</span>
                    <span>Daily Action Plan</span>
                  </h2>
                )}
                <div className="flex items-center gap-4">
                  {insights.actions && insights.actions.length > 0 && (
                    <button onClick={handlePrintInsights} className="text-[#00D4FF] text-sm hover:text-[#00E676]">Print</button>
                  )}
                  <button onClick={loadInsights} className="text-[#00D4FF] text-sm hover:text-[#00E676]">Refresh</button>
                </div>
              </div>
              {insights.actions.length === 0 ? (
                <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-5 text-gray-400 text-sm">
                  No specific actions to recommend right now.
                </div>
              ) : (
                <div className={`overflow-hidden transition-all duration-300 ease-in-out ${insightsExpanded ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                  <div className="space-y-3">
                  {insights.actions.map((a, i) => {
                    const matched = a.buyer_name
                      ? buyers.find(b => (b.name || '').trim().toLowerCase() === a.buyer_name.trim().toLowerCase())
                      : null;
                    const borderClass =
                      a.priority === 1 ? 'border-l-[#00E676]' :
                      a.priority <= 3  ? 'border-l-[#00D4FF]' :
                                         'border-l-gray-500';
                    const numClass =
                      a.priority === 1 ? 'bg-[#00E676]/20 text-[#00E676] border-[#00E676]/40' :
                      a.priority <= 3  ? 'bg-[#00D4FF]/20 text-[#00D4FF] border-[#00D4FF]/40' :
                                         'bg-gray-500/20 text-gray-400 border-gray-500/40';
                    return (
                      <div key={i} className={`bg-[#1A2744] rounded-r-xl border-l-4 ${borderClass} border-y border-r border-[#546E7A]/30 p-4`}>
                        <div className="flex items-start gap-3">
                          <div className={`shrink-0 w-7 h-7 rounded-full border ${numClass} flex items-center justify-center text-xs font-bold`}>
                            {a.priority}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm leading-snug">
                              {a.buyer_name && (
                                matched
                                  ? <Link to={`/match-board?buyer=${matched.id}`} className="text-[#00D4FF] hover:underline font-medium mr-1.5">{a.buyer_name}</Link>
                                  : <span className="text-[#00D4FF] font-medium mr-1.5">{a.buyer_name}</span>
                              )}
                              <span>{a.action}</span>
                            </p>
                            {a.reasoning && (
                              <p className="text-gray-500 text-xs mt-1.5">{a.reasoning}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-5">
            <p className="text-gray-400 text-sm">Active Buyers</p>
            <p className="text-white text-2xl md:text-3xl font-bold mt-1">{buyers.length}</p>
            <Link to="/buyers" className="text-[#00D4FF] text-sm mt-2 inline-block hover:text-[#00E676]">Manage Buyers →</Link>
          </div>
          <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-5">
            <p className="text-gray-400 text-sm">Properties</p>
            <p className="text-white text-2xl md:text-3xl font-bold mt-1">{properties.length}</p>
            <Link to="/properties" className="text-[#00D4FF] text-sm mt-2 inline-block hover:text-[#00E676]">Manage Properties →</Link>
          </div>
          <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-5">
            <p className="text-gray-400 text-sm">Recent Evaluations</p>
            <p className="text-white text-2xl md:text-3xl font-bold mt-1">{recentGrades.length}</p>
            <Link to="/match-board" className="text-[#00D4FF] text-sm mt-2 inline-block hover:text-[#00E676]">View Match Board →</Link>
          </div>
        </div>

        {/* Activity Feed */}
        <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-5 mb-8">
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-white font-semibold">Activity Feed</h2>
            <span className="text-gray-500 text-xs">{activity.length} recent</span>
          </div>
          {activity.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-gray-500 text-sm">No activity yet.</p>
              <p className="text-gray-600 text-xs mt-1">Your buyers' notes, signups, and overrides will appear here.</p>
            </div>
          ) : (
            <>
              <div className="divide-y divide-[#546E7A]/10">
                {activity.map(item => (
                  <ActivityItem key={item.id} item={item} />
                ))}
              </div>
              {activityHasMore && (
                <div className="text-center mt-3 pt-3 border-t border-[#546E7A]/10">
                  <Link to="/agent-board" className="text-[#00D4FF] text-sm hover:text-[#00E676]">View All →</Link>
                </div>
              )}
            </>
          )}
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Buyers */}
          <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-5">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-white font-semibold">Your Buyers</h2>
              <Link to="/buyers" className="text-[#00D4FF] text-sm hover:text-[#00E676]">View All →</Link>
            </div>
            {buyers.length === 0 ? (
              <p className="text-gray-500 text-sm">No buyers yet. Add your first buyer to start evaluating.</p>
            ) : (
              <div className="space-y-2">
                {buyers.slice(0, 5).map(buyer => (
                  <Link key={buyer.id} to={`/match-board?buyer=${buyer.id}`}
                    className="flex justify-between items-center gap-3 p-3 bg-[#0A0E27]/50 rounded-lg hover:bg-[#243B55]/30 transition-colors">
                    <div className="min-w-0">
                      <div className="text-white text-sm font-medium truncate">{buyer.name}</div>
                      <div className="text-gray-500 text-xs">{buyer.criteria_count} criteria · {buyer.properties_graded} properties evaluated</div>
                    </div>
                    <span className="text-gray-600 shrink-0">→</span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Recent Evaluations */}
          <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-5">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-white font-semibold">Recent Evaluations</h2>
              <Link to="/match-board" className="text-[#00D4FF] text-sm hover:text-[#00E676]">View Board →</Link>
            </div>
            {recentGrades.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-gray-500 text-sm">No evaluations yet.</p>
                <p className="text-gray-600 text-xs mt-1">Add buyers and properties, then start grading.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {recentGrades.map((grade, i) => (
                  <div key={i} className="p-3 bg-[#0A0E27]/50 rounded-lg">
                    <div className="text-white text-sm">{grade.address || 'Property'}</div>
                    <div className="text-gray-500 text-xs">{grade.buyer_name || 'Buyer'} · Score: {grade.score || 'N/A'}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Match Board CTA */}
        <div className="bg-[#1A2744] rounded-xl border border-[#00D4FF]/20 p-6 md:p-8 text-center">
          <h2 className="text-white font-bold text-xl mb-2">Your Property Match Board</h2>
          <p className="text-gray-400 mb-4">View your ranked properties and AI evaluation reports.</p>
          <Link to="/match-board" className="inline-block px-6 py-3 bg-[#00D4FF] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00B8D4] transition-colors">
            View Match Board
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const { user } = useAuth();

  // Brokerage brokers land on their broker experience (dashboard), not the agent home.
  if (user?.account_type === 'brokerage_user' && user?.role === 'broker') {
    return <Navigate to="/broker-dashboard" replace />;
  }

  if (user?.role === 'buyer') {
    return <BuyerHome user={user} />;
  }

  return <AgentHome user={user} />;
}
